"use client";

// "Cómo funciona" con el modo "story" del handoff: un puntero automático
// (`setStepsStory`, 2.4s por paso) recorre los 4 pasos, resalta el activo
// (`.pm-lit`, borde violeta) y va "jugando" el micro-arte de cada paso
// (`.pm-play`) a medida que pasa — más la línea conectora que se rellena al
// entrar en viewport (`setStepsLine`) y el número fantasma de fondo
// (`setStepsGhost`). Todo esto está ON por default en TWEAK_DEFAULTS.

import { useEffect, useRef, useState } from "react";
import { L } from "./tokens";
import { ScrollReveal } from "./ScrollReveal";

const STEPS = [
  {
    n: 1, title: "Abrís la búsqueda",
    desc: "Cargás el puesto en 2 minutos. Etapas, palabras clave, preguntas custom. Publicás al portal con un click.",
  },
  {
    n: 2, title: "Llegan postulaciones",
    desc: "Cada CV entra al pipeline desde tu portal, LinkedIn, Bumerán o referidos. La IA lo parsea apenas se sube.",
  },
  {
    n: 3, title: "Revisás solo a los top",
    desc: "Filtrás por fit ≥85, leés el resumen del CV en 30 segundos y movés al candidato a la siguiente etapa.",
  },
  {
    n: 4, title: "Cerrás con datos",
    desc: "Scorecards por entrevistador, oferta firmable online y métricas de funnel listas para la próxima búsqueda.",
  },
];

function StepArt({ n, played }: { n: number; played: boolean }) {
  if (n === 1) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="overflow-hidden rounded-lg bg-white px-2.5 py-1.5" style={{ border: `1px solid ${L.line}` }}>
          <span
            style={{
              display: "inline-block", overflow: "hidden", whiteSpace: "nowrap", verticalAlign: "bottom",
              fontSize: 11, color: L.ink,
              maxWidth: played ? "100%" : 0,
              animation: played ? "tl-type .9s steps(22) .1s forwards" : "none",
            }}
          >
            Senior Product Manager
          </span>
        </div>
        <div className="flex gap-1.5">
          {[
            { t: "React", delay: 450 },
            { t: "Senior", delay: 600 },
            { t: "Remoto", delay: 750, lime: true },
          ].map((tag) => (
            <span
              key={tag.t}
              className="rounded-full px-2 py-0.5"
              style={{
                background: tag.lime ? `${L.lime}55` : L.violetSoft, color: tag.lime ? "#5C6B00" : L.violet,
                fontSize: 10, fontWeight: 600,
                opacity: played ? 1 : 0, transform: played ? "none" : "translateY(6px) scale(.9)",
                transition: `opacity .35s ${tag.delay}ms, transform .35s cubic-bezier(.34,1.56,.64,1) ${tag.delay}ms`,
              }}
            >
              {tag.t}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-lg py-1.5 px-2.5" style={{ border: `1px solid ${L.line}`, color: L.inkFaint, fontSize: 10.5 }}>Descripción…</div>
          <span
            className="rounded-md px-2.5 py-1.5 text-white"
            style={{
              background: L.ink, fontSize: 10.5, fontWeight: 600,
              opacity: played ? 1 : 0, transform: played ? "none" : "translateY(6px) scale(.9)",
              transition: "opacity .35s 1000ms, transform .35s cubic-bezier(.34,1.56,.64,1) 1000ms",
              animation: played ? "tl-btnclick .3s ease 1.5s" : "none",
            }}
          >
            Publicar
          </span>
        </div>
      </div>
    );
  }
  if (n === 2) {
    const rows: [string, number, number, number][] = [["Postulado", 100, 42, 150], ["Revisión", 62, 26, 300], ["Terna", 33, 14, 450], ["Entrev.", 17, 7, 600]];
    return (
      <div className="flex flex-col gap-1.5">
        {rows.map(([lbl, w, v, delay]) => (
          <div key={lbl} className="flex items-center gap-2">
            <span style={{ fontSize: 10, color: L.inkSoft, width: 48, flexShrink: 0 }}>{lbl}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full" style={{ background: L.line }}>
              <i
                className="block h-full rounded-full"
                style={{ width: played ? `${w}%` : 0, background: L.violet, transition: `width 1s cubic-bezier(.22,1,.36,1) ${delay}ms` }}
              />
            </div>
            <span style={{ fontFamily: L.fontMono, fontSize: 10, color: L.ink, width: 18, flexShrink: 0 }}>{v}</span>
          </div>
        ))}
      </div>
    );
  }
  if (n === 3) {
    const rows: [string, string, number, string, number][] = [["LF", "Lucía Ferreyra", 94, L.violet, 200], ["MR", "Martín Ríos", 91, L.green, 450]];
    return (
      <div className="flex flex-col gap-2">
        {rows.map(([av, nm, sc, color, delay]) => (
          <div
            key={nm}
            className="flex items-center gap-2.5 rounded-lg bg-white px-2.5 py-1.5"
            style={{
              border: `1px solid ${L.line}`,
              opacity: played ? 1 : 0, transform: played ? "none" : "translateX(18px)",
              transition: `opacity .4s ${delay}ms, transform .45s cubic-bezier(.22,1,.36,1) ${delay}ms`,
            }}
          >
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-white" style={{ background: color, fontSize: 9.5, fontWeight: 700 }}>{av}</div>
            <span className="flex-1" style={{ fontSize: 11.5, color: L.ink, fontWeight: 500 }}>{nm}</span>
            <span style={{ fontFamily: L.fontMono, fontSize: 11.5, fontWeight: 700, color }}>{sc}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1">
      <span
        className="rounded border-2 px-3 py-1"
        style={{
          fontFamily: L.fontDisplay, fontSize: 18, fontWeight: 700, color: L.green, borderColor: L.green, rotate: "-4deg",
          animation: played ? "tl-stamp .5s cubic-bezier(.34,1.56,.64,1) .35s forwards" : "none",
          opacity: played ? undefined : 0,
        }}
      >
        HIRED
      </span>
      <span style={{ fontSize: 10.5, color: L.inkSoft, opacity: played ? 1 : 0, transition: "opacity .4s 900ms" }}>Lucía Ferreyra · 21 días</span>
    </div>
  );
}

export function HowItWorks() {
  const [storyIdx, setStoryIdx] = useState(0);
  const [cycleId, setCycleId] = useState(0);
  const [lineOn, setLineOn] = useState(false);
  const hoverRef = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) { setTimeout(() => setLineOn(true), 250); io.unobserve(e.target); } }); },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      if (hoverRef.current) return;
      setStoryIdx((i) => {
        const next = (i + 1) % STEPS.length;
        if (next === 0) setCycleId((c) => c + 1);
        return next;
      });
    }, 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="como-funciona" style={{ background: L.bg, padding: "110px 0" }}>
      <div className="mx-auto max-w-[1240px] px-8">
        <ScrollReveal>
          <div className="mx-auto max-w-[720px] text-center">
            <h2 style={{ fontFamily: L.fontDisplay, fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 0.98, fontSize: "clamp(40px, 5.2vw, 68px)", color: L.ink, margin: "0 0 18px" }}>
              De brief a hire,<br /><em style={{ fontStyle: "normal", color: L.inkFaint }}>en cuatro pasos.</em>
            </h2>
            <p className="mx-auto" style={{ fontSize: 19, lineHeight: 1.45, color: L.inkSoft }}>
              Sin migraciones eternas, sin importar planillas. Tu primera búsqueda corre el mismo día.
            </p>
          </div>
        </ScrollReveal>

        <div
          ref={wrapRef}
          onMouseEnter={() => { hoverRef.current = true; }}
          onMouseLeave={() => { hoverRef.current = false; }}
          className="relative mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {/* Línea conectora — se rellena al entrar en viewport */}
          <div className="pointer-events-none absolute left-0 right-0 z-0 hidden lg:block" style={{ top: 38, height: 2 }}>
            <i className="block h-full rounded-full" style={{ width: lineOn ? "100%" : "0%", background: `linear-gradient(90deg, ${L.violet}, ${L.lime})`, transition: "width 1.6s cubic-bezier(.22,1,.36,1)" }} />
          </div>

          {STEPS.map((s, i) => {
            const lit = i === storyIdx;
            const played = i <= storyIdx;
            return (
              <ScrollReveal key={s.n} delay={i * 80}>
                <div
                  className="relative z-[1] flex h-full flex-col overflow-visible rounded-2xl p-6 transition-transform hover:-translate-y-[5px]"
                  style={{
                    border: `1px solid ${lit ? L.violet : L.line}`,
                    background: "#fff",
                    boxShadow: lit ? `0 0 0 1px ${L.violet}, 0 18px 40px -18px rgba(107,92,255,.4)` : undefined,
                    transition: "border-color .25s, box-shadow .3s ease, transform .25s cubic-bezier(.22,1,.36,1)",
                  }}
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute select-none"
                    style={{
                      right: -6, top: -28, fontFamily: L.fontDisplay, fontWeight: 700, fontSize: 150, lineHeight: 1,
                      color: L.violet, opacity: 0.06, zIndex: 0,
                    }}
                  >
                    {s.n}
                  </span>
                  <div className="relative z-[1] mb-3 flex items-center gap-2">
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-full text-white transition-transform"
                      style={{ background: lit ? L.violet : L.violet, fontFamily: L.fontDisplay, fontSize: 12, fontWeight: 700 }}
                    >
                      {s.n}
                    </span>
                    <span style={{ fontFamily: L.fontMono, fontSize: 11, color: L.inkFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>Paso {s.n}</span>
                  </div>
                  <h3 className="relative z-[1]" style={{ fontFamily: L.fontDisplay, fontSize: 22, fontWeight: 700, color: L.ink, margin: "0 0 8px" }}>{s.title}</h3>
                  <p className="relative z-[1]" style={{ fontSize: 14.5, color: L.inkSoft, lineHeight: 1.5, flex: 1 }}>{s.desc}</p>
                  <div className="relative z-[1] mt-4 flex items-center rounded-xl px-3" style={{ height: 110, background: L.bgWarm }}>
                    <div key={cycleId} className="w-full">
                      <StepArt n={s.n} played={played} />
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
@keyframes tl-type { to { max-width: 100%; } }
@keyframes tl-btnclick { 50% { transform: scale(.92); } }
@keyframes tl-stamp { from { opacity: 0; transform: scale(2.2) rotate(8deg); } 70% { opacity: 1; transform: scale(.94) rotate(-5deg); } to { opacity: 1; transform: scale(1) rotate(-4deg); } }
@media (prefers-reduced-motion: reduce) { .tl-step-art * { animation: none !important; transition: none !important; } }
`,
        }}
      />
    </section>
  );
}
